import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Loader } from "@/components/brand/Mascot";
import { Brush } from "@/components/brand/Lettering";
import { Countdown } from "@/components/Countdown";
import { Reveal } from "@/components/Reveal";
import { getDrops } from "@/lib/api";
import { WaitlistForm } from "@/components/WaitlistForm";
import { breadcrumb } from "@/lib/site";

const STATUS = { sold_out: "Sold out", live: "Live now", coming_soon: "Coming soon", upcoming: "Upcoming" };
const FILTERS = [["all", "All drops"], ["live", "Live"], ["sold_out", "Sold out"], ["coming_soon", "Coming soon"], ["upcoming", "Upcoming"]];

const ACCENT = ["bg-lime", "bg-pink", "bg-sky"];

const DropCard = ({ d, i }) => (
  <Reveal as="article" delay={i * 90} className="flex flex-col" data-testid={`drop-${d.number}`}>
    <Link to={`/drops/${d.slug}`} aria-label={`Drop ${d.number}, ${d.name}`} className={`relative block aspect-[3/4] overflow-hidden rounded-[24px] border-[1.5px] border-ink ${ACCENT[i % 3]}`} data-testid={`drop-accent-${d.number}`}>
      <img src={d.image} alt={`Drop ${d.number}, ${d.name} campaign photo`} loading="lazy" className={`photo h-full w-full object-cover mix-blend-multiply ${d.status === "upcoming" ? "scale-105 blur-md" : ""} ${d.status === "sold_out" ? "opacity-70" : ""}`} />
      {d.status === "sold_out" && (
        <div className="absolute bottom-6 left-6" data-testid="drop-sold-out-overlay">
          <Brush lines={["Sold out"]} className="text-5xl text-cloud" />
          <svg viewBox="0 0 160 14" className="-mt-1 h-3 w-36 text-lime" aria-hidden="true"><path d="M3 9c40-8 90 4 154-3" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" /></svg>
        </div>
      )}
      {d.status === "upcoming" && <span className="label absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-cloud px-4 py-2 font-bold">Under wraps</span>}
    </Link>
    <div className="mt-6 flex items-start justify-between gap-4 border-b border-ink/10 pb-5">
      <div>
        <p className="label text-ink/50">Drop {d.number} · {d.release_label}</p>
        <h2 className="display mt-3 text-[clamp(2.25rem,3.4vw,3.25rem)]"><Link to={`/drops/${d.slug}`} className="underline-offset-8 hover:underline" data-testid={`drop-page-link-${d.number}`}>{d.name}</Link></h2>
      </div>
      <span className={`sticker shrink-0 ${ACCENT[i % 3]}`} data-testid={`drop-status-${d.number}`}>{STATUS[d.status]}</span>
    </div>
    <p className="mt-5 text-[15px] leading-relaxed text-ink/70">{d.description}</p>
    <div className="mt-6">
      <Link to={`/drops/${d.slug}`} className="link-u mb-6 mr-6 inline-flex" data-testid={`drop-view-${d.number}`}>View the drop <ArrowRight size={14} /></Link>
      {(d.status === "sold_out" || d.status === "live") && <Link to={`/shop?drop=${d.slug}`} className="link-u" data-testid={`drop-shop-${d.number}`}>{d.status === "live" ? "Shop the drop" : "Shop the restock"} <ArrowRight size={14} /></Link>}
      {d.status === "coming_soon" && (
        <div className="space-y-6">
          <Countdown size="sm" target={d.release_date} testid="drops-countdown" />
          <WaitlistForm drop={d} testid={`drop-waitlist-${d.number}`} />
        </div>
      )}
      {d.status === "upcoming" && <div className="space-y-5"><p className="label text-ink/50">Details dropping soon</p><WaitlistForm drop={d} testid={`drop-waitlist-${d.number}`} /></div>}
    </div>
  </Reveal>
);

export default function Drops() {
  const [drops, setDrops] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { getDrops().then(setDrops).catch(() => setDrops([])); }, []);
  const shown = (drops || []).filter((d) => filter === "all" || d.status === filter);
  return (
    <div className="mx-auto max-w-[1440px] px-5 pb-28 sm:px-10" data-testid="drops-page">
      <Seo title="Drops: limited releases" description="Limited drops from the suspects, Toronto streetwear. Drop 001 The Usual Suspects sold out. Drop 002 Sus World lands Nov 15, 2026 at 7PM ET. Drop 003 Good People, Nov 2026." path="/drops" jsonLd={[breadcrumb([{ name: "Home", path: "/" }, { name: "Drops", path: "/drops" }])]} />
      <header className="grid items-end gap-8 pb-14 pt-16 lg:grid-cols-12 lg:pt-24">
        <h1 className="display text-[clamp(6rem,20vw,17rem)] lg:col-span-8">Drops</h1>
        <div className="lg:col-span-4 lg:justify-self-end lg:text-right">
          <p className="label font-bold">New ideas.<br />Same sus energy.</p>
          <span className="tri-stripe mt-4 block w-24 lg:ml-auto" />
        </div>
      </header>
      <div className="flex gap-8 overflow-x-auto border-b border-ink/10 no-scrollbar" role="tablist" aria-label="Filter drops">
        {FILTERS.map(([id, l]) => (
          <button key={id} role="tab" aria-selected={filter === id} onClick={() => setFilter(id)} className={`label -mb-px shrink-0 border-b-[1.5px] pb-4 font-bold transition-colors ${filter === id ? "border-ink text-ink" : "border-transparent text-ink/45 hover:text-ink"}`} data-testid={`drops-filter-${id}`}>{l}</button>
        ))}
      </div>
      {drops === null ? <Loader /> : (
        <div className="mt-12 grid gap-x-6 gap-y-16 md:grid-cols-3">{shown.map((d, i) => <DropCard key={d.slug} d={d} i={i} />)}</div>
      )}
    </div>
  );
}
